import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from '@iveco-ti/iveco-ds/hook-form'

import { ApplicationAuthModel } from '@/common/models/application'
import {
  ChangePasswordForm as ChangePasswordFormType,
  ChangePasswordSchema,
  EditCredentialsForm as EditCredentialsFormType,
  EditCredentialsSchema,
} from '../schemas/EditCredentialsSchema'
import { useEditApplicationAuthCredentials } from './useEditApplicationAuthCredentials'
import { useEditApplicationAuthPassword } from './useEditApplicationAuthPassword'

type EditMode = 'view' | 'edit-credentials' | 'edit-password'

type UseDrawerManageAuthFormsProps = {
  authData: ApplicationAuthModel | null | undefined
  editMode: EditMode
  setEditMode: (mode: EditMode) => void
}

export function useDrawerManageAuthForms({
  authData,
  editMode,
  setEditMode,
}: UseDrawerManageAuthFormsProps) {
  const { handleEditCredentials } = useEditApplicationAuthCredentials()
  const { handleEditPassword } = useEditApplicationAuthPassword()

  const credentialsForm = useForm<EditCredentialsFormType>({
    resolver: zodResolver(EditCredentialsSchema()),
    defaultValues: {
      clientKey: '',
      currentPassword: '',
    },
  })

  const passwordForm = useForm<ChangePasswordFormType>({
    resolver: zodResolver(ChangePasswordSchema()),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmNewPassword: '',
    },
  })

  const handleSubmitCredentials = async (data: EditCredentialsFormType) => {
    if (!authData) return
    const credentialsData = {
      authId: authData.id,
      applicationId: authData.applicationId,
      clientKey: data.clientKey,
      newPassword: data.currentPassword,
      subId: authData.subId,
      headers: authData.headers,
      metadata: authData.metadata,
    }
    try {
      await handleEditCredentials(credentialsData)
      setEditMode('view')
    } catch {
      setEditMode('view')
    }
  }

  const handleSubmitPassword = async (data: ChangePasswordFormType) => {
    if (!authData) return
    const passwordData = {
      applicationId: authData.applicationId,
      clientKey: authData.clientKey,
      currentPassword: data.currentPassword,
      newPassword: data.newPassword,
    }
    await handleEditPassword(passwordData)
    setEditMode('view')
  }

  useEffect(() => {
    if (authData?.clientKey && editMode === 'edit-credentials') {
      credentialsForm.reset(
        {
          clientKey: authData.clientKey,
          currentPassword: '',
        },
        { keepDirtyValues: false },
      )
    }
  }, [authData, credentialsForm, editMode])

  const resetForms = () => {
    passwordForm.reset()
    credentialsForm.reset()
  }

  return {
    credentialsForm,
    passwordForm,
    handleSubmitCredentials,
    handleSubmitPassword,
    resetForms,
  }
}
